import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Pencil, Save, Trash2, UserPlus, X } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { useOrganizationQuery, useUpdateOrganizationMutation, useDeleteOrganizationMutation, useMyOrganizationRolesQuery } from "@/hooks/queries/useOrganizationsQuery";
import { RoleType } from "@/types/project";
import type { DeleteOrganizationCommand, Organization, UpdateOrganizationCommand } from "@/types/organization";
import OverviewTab from "@/components/organization/OverviewTab";
import OrganizationUsersTab from "@/components/organization/OrganizationUsersTab";
import OrganizationProjectsTab from "@/components/organization/OrganizationProjectsTab";
import PaymentsTab from "@/components/organization/PaymentsTab";
import InviteOrganizationUserDialog from "@/components/organization/InviteOrganizationUserDialog";
import { toast } from "sonner";

const OrganizationDetail = () => {
  const { id } = useParams<{ id: string }>();
  const organizationId = Number(id);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const { data: organization, isLoading } = useOrganizationQuery(organizationId);
  const { data: myRoles = {} } = useMyOrganizationRolesQuery();
  const updateOrganizationMutation = useUpdateOrganizationMutation();
  const deleteOrganizationMutation = useDeleteOrganizationMutation();
  const [editing, setEditing] = useState(false);
  const validTabs = ["overview", "users", "projects", "payments"];
  const tabParam = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(tabParam && validTabs.includes(tabParam) ? tabParam : "overview");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", description: "", orgNumber: "", contactEmail: "", country: "", owner: "" });

  const role = organization ? myRoles[organization.id] : undefined;
  const canManage = role === RoleType.Owner || role === RoleType.Editor;
  const canDelete = role === RoleType.Owner;
  const canInvite = role === RoleType.Owner;
  const canMoveProjects = role === RoleType.Owner;
  const deleteBlocked = organization ? organization.creditBalance > 0 || organization.projectCount > 0 : true;
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  const populateEditForm = (value: Organization) => {
    setEditForm({
      name: value.name,
      description: value.description || "",
      orgNumber: value.orgNumber || "",
      contactEmail: value.contactEmail || "",
      country: value.country || "",
      owner: value.owner || "",
    });
  };

  useEffect(() => {
    if (organization) populateEditForm(organization);
  }, [organization]);

  useEffect(() => {
    setActiveTab(tabParam && validTabs.includes(tabParam) ? tabParam : "overview");
  }, [tabParam]);

  const handleSave = async () => {
    if (!organization) return;
    const command: UpdateOrganizationCommand = {
      id: organization.id,
      name: editForm.name,
      description: editForm.description || null,
      orgNumber: editForm.orgNumber || null,
      contactEmail: editForm.contactEmail || null,
      country: editForm.country || null,
      owner: editForm.owner || null,
      rowVersion: organization.rowVersion,
    };
    await updateOrganizationMutation.mutateAsync({ id: organization.id, command });
    setEditing(false);
  };

  const handleDeleteClick = () => {
    if (deleteBlocked) {
      toast.error(t("organizationDeleteBlocked"));
      return;
    }
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!organization) return;
    const command: DeleteOrganizationCommand = { id: organization.id, rowVersion: organization.rowVersion };
    await deleteOrganizationMutation.mutateAsync({ id: organization.id, command });
    navigate("/organization");
  };

  if (isLoading) {
    return <DashboardLayout><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-muted-foreground" size={32} /></div></DashboardLayout>;
  }

  if (!organization) {
    return <DashboardLayout><div className="flex items-center justify-center h-64"><p className="text-muted-foreground">{t("organizationNotFound")}</p></div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/organization")} aria-label={t("organizations")}><ArrowLeft size={20} /></Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{organization.name}</h1>
              <p className="text-muted-foreground">
                {t("organizationCreditBalance")}: {organization.creditBalance.toFixed(2)} • {t("organizationProjects")}: {organization.projectCount}
              </p>
            </div>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            {!editing ? (
              <>
                {canInvite && <Button variant="outline" size="sm" className="w-full gap-1 sm:w-auto" onClick={() => setShowInviteDialog(true)}><UserPlus size={16} />{t("inviteMember")}</Button>}
                {canManage && <Button variant="outline" size="sm" className="w-full gap-1 sm:w-auto" onClick={() => setEditing(true)}><Pencil size={16} />{t("edit")}</Button>}
                {canDelete && <Button variant="outline" size="sm" className="w-full gap-1 text-destructive hover:text-destructive sm:w-auto" onClick={handleDeleteClick} disabled={deleteBlocked}><Trash2 size={16} />{t("delete")}</Button>}
              </>
            ) : (
              <>
                <Button size="sm" className="w-full gap-1 sm:w-auto" onClick={handleSave} disabled={updateOrganizationMutation.isPending}>{updateOrganizationMutation.isPending ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}{t("save")}</Button>
                <Button variant="outline" size="sm" className="w-full gap-1 sm:w-auto" onClick={() => { populateEditForm(organization); setEditing(false); }} disabled={updateOrganizationMutation.isPending}><X size={16} />{t("cancel")}</Button>
              </>
            )}
          </div>
        </div>

        {deleteBlocked && canDelete ? <p className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">{t("organizationDeleteBlocked")}</p> : null}

        {editing ? (
          <div className="bg-card p-6 rounded-lg border border-border space-y-4">
            <div className="space-y-2"><Label>{t("organizationName")} *</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required /></div>
            <div className="space-y-2"><Label>{t("description")}</Label><Textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>{t("organizationNumber")}</Label><Input value={editForm.orgNumber} onChange={(e) => setEditForm({ ...editForm, orgNumber: e.target.value })} /></div>
              <div className="space-y-2"><Label>{t("contactEmail")}</Label><Input type="email" value={editForm.contactEmail} onChange={(e) => setEditForm({ ...editForm, contactEmail: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>{t("country")}</Label><Input value={editForm.country} onChange={(e) => setEditForm({ ...editForm, country: e.target.value })} /></div>
              <div className="space-y-2"><Label>{t("organizationOwner")}</Label><Input value={editForm.owner} onChange={(e) => setEditForm({ ...editForm, owner: e.target.value })} /></div>
            </div>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full justify-start overflow-x-auto flex-wrap h-auto gap-1 bg-transparent p-0 border-b rounded-none">
              <TabsTrigger value="overview" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none data-[state=active]:shadow-none">{t("tabOverview")}</TabsTrigger>
              <TabsTrigger value="users" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none data-[state=active]:shadow-none">{t("organizationUsers")}</TabsTrigger>
              <TabsTrigger value="projects" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none data-[state=active]:shadow-none">{t("organizationProjects")}</TabsTrigger>
              <TabsTrigger value="payments" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none data-[state=active]:shadow-none">{t("tabPayments")}</TabsTrigger>
            </TabsList>
            <div className="mt-6">
              <TabsContent value="overview" className="m-0"><OverviewTab organization={organization} /></TabsContent>
              <TabsContent value="users" className="m-0"><OrganizationUsersTab organizationId={organization.id} /></TabsContent>
              <TabsContent value="projects" className="m-0"><OrganizationProjectsTab organizationId={organization.id} canMoveProjects={canMoveProjects} /></TabsContent>
              <TabsContent value="payments" className="m-0"><PaymentsTab organizationId={organization.id} canEdit={canManage} canTransfer={role === RoleType.Owner} creditBalance={organization.creditBalance} lastCreditsUpdatedAt={organization.lastCreditsUpdatedAt} /></TabsContent>
            </div>
          </Tabs>
        )}
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirmDeleteOrganization")}</AlertDialogTitle>
            <AlertDialogDescription>{t("confirmDeleteOrganizationDesc")} <strong>{organization.name}</strong>?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteOrganizationMutation.isPending}>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleteOrganizationMutation.isPending} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleteOrganizationMutation.isPending && <Loader2 className="animate-spin mr-2" size={16} />}{t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {canInvite && (
        <InviteOrganizationUserDialog organizationId={organization.id} open={showInviteDialog} onOpenChange={setShowInviteDialog} />
      )}
    </DashboardLayout>
  );
};

export default OrganizationDetail;
